import { useCallback, useEffect, useState } from 'react';
import { effectiveProductSearchQuery, PRODUCT_SEARCH_DEBOUNCE_MS } from '../../../shared/product-search/productSearchUx';
import { useDebouncedValue } from '../../../shared/ui/hooks-ui/useDebouncedValue';
import { STOCK_PAGE_SIZE } from '../../application/inventoryUtils';
import type { StockRow, TransferRow } from '../../../inventory/stock/public-api';
import { useInventoryUiStore } from '../../store/index';
import { applyInventoryUserNotice, handleInventoryFlowResult } from '../../application/inventoryFlowBridge';
import type { InventoryRefreshHandlers } from '../../application/inventoryFlowBridge';
import { useInventoryOperationsController } from './useInventoryOperationsController';

type Params = {
  ctx: {
    currentBranchId: string | null | undefined;
    user: { id?: string } | null | undefined;
    canEdit: boolean;
    canTransfer?: boolean;
    destinationBranches: Array<{ id: string }>;
  };
  refresh: InventoryRefreshHandlers;
};

export function useInventoryTransferFlow({ ctx, refresh }: Params) {
  const controller = useInventoryOperationsController();
  const branchId = ctx.currentBranchId ?? null;
  const editCtx = { canEdit: ctx.canEdit, canTransfer: ctx.canTransfer, branchId, userId: ctx.user?.id ?? null };

  const [transferError, setTransferError] = useState<string | null>(null);
  const [transferPickerStocks, setTransferPickerStocks] = useState<StockRow[]>([]);
  const [transferPickerLoading, setTransferPickerLoading] = useState(false);
  const [transferPickerLoadingMore, setTransferPickerLoadingMore] = useState(false);
  const [transferPickerOffset, setTransferPickerOffset] = useState(0);
  const [transferPickerHasMore, setTransferPickerHasMore] = useState(false);

  const {
    transferPickerVisible,
    setTransferPickerVisible,
    transferPickerSearch,
    setTransferPickerSearch,
    selectedStock: transferSelectedStock,
    setSelectedStock: setTransferSelectedStock,
    destinationBranchId,
    setDestinationBranchId,
    transferQuantity,
    setTransferQuantity,
    transferWaitForReceive,
    setTransferWaitForReceive,
    setIsSubmittingTransfer,
    setTransferModalVisible,
    processingTransferId,
    setProcessingTransferId,
    setPendingTransfersModalVisible,
  } = useInventoryUiStore();

  const transferPickerSearchDebounced = useDebouncedValue(transferPickerSearch.trim(), PRODUCT_SEARCH_DEBOUNCE_MS);

  // UX audit: el error de transferencia no puede quedar pegado después de que
  // el usuario corrige el campo que lo causó (cantidad, destino, modo).
  useEffect(() => {
    setTransferError((current) => (current === null ? current : null));
  }, [destinationBranchId, transferQuantity, transferWaitForReceive, transferSelectedStock]);

  const resetTransferPickerStocks = useCallback(() => {
    setTransferPickerStocks((current) => (current.length === 0 ? current : []));
    setTransferPickerOffset(0);
    setTransferPickerHasMore(false);
    setTransferPickerLoading(false);
    setTransferPickerLoadingMore(false);
  }, []);

  useEffect(() => {
    if (!transferPickerVisible || !branchId) {
      // Al cerrar el picker no conservar filas: reabrirlo mostraría
      // cantidades stale mientras vuelve a cargar.
      resetTransferPickerStocks();
      return;
    }
    const search = effectiveProductSearchQuery(transferPickerSearchDebounced);
    if (!search) {
      setTransferPickerStocks([]);
      setTransferPickerOffset(0);
      setTransferPickerHasMore(false);
      setTransferPickerLoading(false);
      setTransferPickerLoadingMore(false);
      return;
    }
    let cancelled = false;
    setTransferPickerLoading(true);
    setTransferPickerStocks([]);
    setTransferPickerHasMore(false);
    void (async () => {
      try {
        const rows = await controller.getStockPage(branchId, { limit: STOCK_PAGE_SIZE, offset: 0, search, includeUnstocked: false });
        if (cancelled) return;
        setTransferPickerStocks(rows);
        setTransferPickerOffset(rows.length);
        setTransferPickerHasMore(rows.length === STOCK_PAGE_SIZE);
      } catch (e) {
        console.warn('[Inventory] No se pudo cargar stock para transferencia', e);
        if (!cancelled) {
          setTransferPickerStocks([]);
          setTransferPickerHasMore(false);
        }
      } finally {
        if (!cancelled) setTransferPickerLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [transferPickerVisible, branchId, transferPickerSearchDebounced, controller, resetTransferPickerStocks]);

  const loadMoreTransferPicker = useCallback(async () => {
    if (!branchId || transferPickerLoadingMore || !transferPickerHasMore || !transferPickerVisible) return;
    const search = effectiveProductSearchQuery(transferPickerSearchDebounced);
    if (!search) return;
    setTransferPickerLoadingMore(true);
    try {
      const rows = await controller.getStockPage(branchId, {
        limit: STOCK_PAGE_SIZE,
        offset: transferPickerOffset,
        search,
        includeUnstocked: false,
      });
      setTransferPickerStocks((current) => [...current, ...rows]);
      setTransferPickerOffset((o) => o + rows.length);
      setTransferPickerHasMore(rows.length === STOCK_PAGE_SIZE);
    } catch (error) {
      console.warn('[Inventory] No se pudo cargar más stock para transferencia', error);
      setTransferPickerHasMore(false);
    } finally {
      setTransferPickerLoadingMore(false);
    }
  }, [branchId, controller, transferPickerLoadingMore, transferPickerHasMore, transferPickerVisible, transferPickerOffset, transferPickerSearchDebounced]);

  const applyTransferModalState = useCallback(
    (stock: StockRow) => {
      setTransferError(null);
      setTransferSelectedStock(stock);
      setTransferQuantity('1');
      setTransferWaitForReceive(false);
      // Sin preselección: destino y modo se eligen explícitos antes de confirmar
      // (audit UX: un tap en Confirmar no puede ejecutar una transferencia).
      setDestinationBranchId(null);
      setTransferModalVisible(true);
    },
    [setDestinationBranchId, setTransferSelectedStock, setTransferModalVisible, setTransferQuantity, setTransferWaitForReceive]
  );

  const openTransferModal = useCallback(
    (stock: StockRow) => {
      const gate = controller.canOpenTransfer({
        ...editCtx,
        destinationBranchCount: ctx.destinationBranches.length,
      });
      if (gate.kind === 'blocked') {
        applyInventoryUserNotice(gate.notice);
        return;
      }
      applyTransferModalState(stock);
    },
    [applyTransferModalState, controller, ctx.destinationBranches.length, editCtx]
  );

  const closeTransferPicker = useCallback(() => {
    setTransferPickerVisible(false);
    setTransferPickerSearch('');
  }, [setTransferPickerSearch, setTransferPickerVisible]);

  const selectStockAndOpenTransferModal = useCallback(
    (stock: StockRow) => {
      closeTransferPicker();
      applyTransferModalState(stock);
    },
    [applyTransferModalState, closeTransferPicker]
  );

  const openTransferEntry = useCallback(() => {
    const gate = controller.canOpenTransfer({
      ...editCtx,
      destinationBranchCount: ctx.destinationBranches.length,
    });
    if (gate.kind === 'blocked') {
      applyInventoryUserNotice(gate.notice);
      return;
    }
    setTransferPickerSearch('');
    setTransferPickerVisible(true);
  }, [controller, ctx.destinationBranches.length, editCtx, setTransferPickerSearch, setTransferPickerVisible]);

  const closeTransferModal = useCallback(() => {
    setTransferModalVisible(false);
    setTransferSelectedStock(null);
    setTransferWaitForReceive(false);
  }, [setTransferModalVisible, setTransferSelectedStock, setTransferWaitForReceive]);

  const handleReceiveTransfer = useCallback(
    async (transfer: TransferRow) => {
      if (processingTransferId != null) return;
      setProcessingTransferId(transfer.id);
      try {
        await handleInventoryFlowResult(await controller.runReceiveTransfer(editCtx, transfer), refresh);
      } finally {
        setProcessingTransferId(null);
      }
    },
    [controller, editCtx, processingTransferId, refresh, setProcessingTransferId]
  );

  const handleRejectTransfer = useCallback(
    async (transfer: TransferRow) => {
      if (processingTransferId != null) return;
      setProcessingTransferId(transfer.id);
      try {
        await handleInventoryFlowResult(await controller.runRejectTransfer(editCtx, transfer), refresh);
      } finally {
        setProcessingTransferId(null);
      }
    },
    [controller, editCtx, processingTransferId, refresh, setProcessingTransferId]
  );

  const handleConfirmTransfer = useCallback(async () => {
    if (useInventoryUiStore.getState().isSubmittingTransfer) return;
    setIsSubmittingTransfer(true);
    setTransferError(null);
    try {
      const result = await controller.runTransfer(
        { ...editCtx, destinationBranchCount: ctx.destinationBranches.length },
        {
          selectedStock: transferSelectedStock,
          destinationBranchId,
          transferQuantity,
          transferWaitForReceive,
          destinationBranchCount: ctx.destinationBranches.length,
          variantId: transferSelectedStock?.variantId ?? null,
        }
      );
      if (result.kind === 'blocked') {
        setTransferError(result.notice?.message ?? 'No pudimos transferir el stock.');
        return;
      }
      closeTransferModal();
      // El stock de origen bajó: el picker no puede mostrar cantidades viejas.
      resetTransferPickerStocks();
      await handleInventoryFlowResult(result, refresh);
    } finally {
      setIsSubmittingTransfer(false);
    }
  }, [
    closeTransferModal,
    controller,
    ctx.destinationBranches.length,
    destinationBranchId,
    editCtx,
    refresh,
    resetTransferPickerStocks,
    setIsSubmittingTransfer,
    transferQuantity,
    transferSelectedStock,
    transferWaitForReceive,
  ]);

  return {
    transferPickerSearchDebounced,
    transferPickerStocks,
    transferPickerLoading,
    transferPickerLoadingMore,
    transferPickerHasMore,
    loadMoreTransferPicker,
    openTransferModal,
    closeTransferModal,
    openTransferEntry,
    closeTransferPicker,
    selectStockAndOpenTransferModal,
    handleConfirmTransfer,
    handleReceiveTransfer,
    handleRejectTransfer,
    openPendingTransfersModal: () => setPendingTransfersModalVisible(true),
    closePendingTransfersModal: () => setPendingTransfersModalVisible(false),
    processingTransferId,
    transferError,
  };
}
